import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { CircleAlert, CircleCheck } from "lucide-react";
import { Alert, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import Spinner from "@/components/Spinner";
import StatusScreen from "@/components/StatusScreen";
import AuthLayout from "@/components/layout/AuthLayout";
import NewPasswordFields, {
  EMPTY_NEW_PASSWORD,
  isValidNewPassword,
} from "../../fragments/NewPasswordFields";
import { acceptPasswordReset } from "../../utils";
import { tokenFromHash } from "../../utils/links";
import { useSession } from "../../session/SessionContext";

const ResetPassword: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { signOut } = useSession();
  const token = tokenFromHash(location.hash);
  const [value, setValue] = useState(EMPTY_NEW_PASSWORD);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
    if (!isValidNewPassword(value)) return;
    setSubmitting(true);
    setError(null);
    try {
      await acceptPasswordReset(token, value.password);
      signOut();
      setDone(true);
    } catch (e) {
      setError((e as Error).message);
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <StatusScreen
        icon={CircleCheck}
        tone="success"
        title="Je wachtwoord is gewijzigd"
        description="Log in met je nieuwe wachtwoord."
        actions={<Button onClick={() => navigate("/login", { replace: true })}>Inloggen</Button>}
      />
    );
  }

  return (
    <AuthLayout title="Kies een nieuw wachtwoord">
      <form className="grid gap-4" noValidate onSubmit={handleSubmit}>
        <NewPasswordFields value={value} onChange={setValue} showErrors={submitted} />
        {error && (
          <Alert variant="destructive">
            <CircleAlert />
            <AlertTitle>{error}</AlertTitle>
          </Alert>
        )}
        <Button type="submit" className="w-full" disabled={submitting}>
          {submitting && <Spinner />}
          Wachtwoord opslaan
        </Button>
      </form>
    </AuthLayout>
  );
};

export default ResetPassword;
